import React, { useRef } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'

import {
  DynamicLoadingPage,
  GenericErrorView,
  routes,
  RouteProps,
} from '@sketch/modules-common'

// Utils
import { removeItem, useScrollTop } from '@sketch/utils'

// Constants
import { localStorageKeys } from '@sketch/constants'

// Hooks
import { useIsProfileCreated } from 'modules/community/hooks'
import {
  useWorkspaceSettings,
  useRefetchWorkspaceSettingsOnRouteChange,
  useGetWorkspaces,
} from 'modules/workspace/operations'

// Local components
import { BillingStatusBanners } from 'modules/workspace/components'

import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers/PrivateWorkspaceRoute'

import {
  WorkspaceSettingsBilling,
  WorkspaceSettingsSso,
} from 'modules/workspace/pages'

// Styles
import { PanelWrapper } from './WorkspaceSettingsView.styles'

// Pages
import { PersonalWorkspaceSettingsPeople } from '../../pages/WorkspaceSettingsPeople'
import { WorkspaceSettingsProfile } from '../../pages/WorkspaceSettingsProfile'
import WorkspaceSettingsLayout from 'modules/workspace/components/WorkspaceSettingsLayout'
import { WorkspaceSettingsPeopleContextProvider } from './WorkspaceSettingsPeopleContext'

// Types
import { useGetWorkspaceMembershipsQuery } from '@sketch/gql-types'
import { workspaceSettingsPermissions } from 'modules/workspace/utils'

interface WorkspaceSettingsProps
  extends RouteProps<'WORKSPACE_SETTINGS'>,
    PrivateWorkspaceRouteExtraProps {}

const WorkspaceSettingsPeople = React.lazy(
  () => import('../../pages/WorkspaceSettingsPeople')
)
const WorkspaceSettingsGeneral = React.lazy(
  () => import('../../pages/WorkspaceSettingsGeneral')
)

/**
 * WorkspaceSettings
 *
 * This is the base component for the Workspace settings
 * It renders
 * - General Section
 * - Billing Section
 * - SSO Section
 */
const WorkspaceSettingsView: React.FC<WorkspaceSettingsProps> = props => {
  const elementToScrollTop = useRef<HTMLElement | null>(null)

  const { workspace } = props
  const { identifier, avatar, name } = workspace
  const workspaceId = workspace.identifier
  const isAdmin = workspace.userRole === 'ADMIN'

  const {
    workspaces,
    loading: workspacesLoading,
    error: hasWorkspacesError,
  } = useGetWorkspaces()

  const {
    data: workspaceSettingsData,
    loading: isWorkspaceSettingsLoading,
    updating: isUpdatingWorkspaceSettings,
    error: hasWorkspaceSettingsError,
    refetch,
  } = useWorkspaceSettings(workspace)

  useRefetchWorkspaceSettingsOnRouteChange(refetch, workspaceId)

  const { workspace: workspaceSettings, customer, sso, billingHistoryCount } =
    workspaceSettingsData || {}

  // Create the customerProvider from the more complete customer object
  // to match the required function query
  const customerProvider = customer
    ? {
        __typename: 'RootQueryType' as const,
        customer,
      }
    : undefined

  const {
    hideNavigation,
    isEducationWorkspace,
    isFinance,
    isIOS,
    isPartner,
    isPersonalWorkspace,
    isGuest,
    showUpsell,
    showWorkspaceSettingsBilling,
    showWorkspaceSettingsGeneral,
    showWorkspaceSettingsSSO,
  } = workspaceSettingsPermissions({
    workspace,
    customerProvider,
    billingHistoryCount,
    settings: workspaceSettings,
    customer,
  })

  // Menu permissions for Workspace Layout
  const canRender = {
    generalMenu: showWorkspaceSettingsGeneral,
    billingMenu: showWorkspaceSettingsBilling,
    SSOMenu: showWorkspaceSettingsSSO,
  }

  // Check for Sketch Partner on the workspace
  const {
    data: sketchPartnerData,
    loading: isLoadingPartnerMembership,
  } = useGetWorkspaceMembershipsQuery({
    variables: {
      workspaceId: workspaceId,
      role: 'PARTNER',
    },
    skip: isPartner,
  })

  // Profile
  const { isProfileCreated } = useIsProfileCreated(workspaceId)

  const loading =
    workspacesLoading ||
    isWorkspaceSettingsLoading ||
    isLoadingPartnerMembership

  const error = hasWorkspacesError || hasWorkspaceSettingsError

  useScrollTop(elementToScrollTop.current)

  if (isGuest) {
    return <Redirect to={routes.WORKSPACE_SHARES.create({ workspaceId })} />
  }

  /**
   * Because workspace and workspaceSettings are fetched in two different queries,
   * after upgrading a legacy workspace, the user can be in a situation where both
   * are out of sync in the cache. "workspace" already says that the workspace has been upgraded
   * but the settings is missing info we are expecting for upgraded workspaces (billing for example)
   * and we need to wait for it to be updated in the cache.
   * https://github.com/sketch-hq/Cloud/issues/8658
   */
  const isUpdatingWorkspaceSettingsInCacheAfterUpgrade =
    workspaceSettings && workspace.type !== workspaceSettings.type

  /**
   * Updates should not show the page spinner unless it's an important one that changes
   * the structure of the page (legacy workspace upgrade)
   */
  if (
    loading &&
    (!isUpdatingWorkspaceSettings ||
      isUpdatingWorkspaceSettingsInCacheAfterUpgrade)
  ) {
    return <DynamicLoadingPage />
  }

  if (error || !workspaceSettings || !workspaces) {
    // This should be taken care by the PrivateWorkspaceRoute already
    // However, due to a different issue: https://github.com/sketch-hq/Cloud/issues/15006
    // The workspace which is invalid might bleed to a deeper component
    //
    // Remove this check when #15006 will be solved:
    removeItem(localStorageKeys.lastWorkspaceIdKey)
    return (
      <GenericErrorView
        error={error}
        // Remove this when #15006 will be solved:
        onClick={() => (window.location.href = '/c')}
      />
    )
  }

  if (isPersonalWorkspace && !workspace.userIsOwner) {
    return (
      <GenericErrorView errorTitle="Only the owner of this workspace can see its settings" />
    )
  }

  const { billing, identifier: customerId, ssoEnabled, ssoShortName } =
    workspaceSettings.customer ?? {}

  const hasSSOFeature = workspaceSettings.features.ssoEnabled

  const { status, trialEnd, seats } = billing ?? {}

  const avatarSrc = avatar?.large || undefined
  const subscriptionEnd = billing?.subscriptionEnd || undefined

  const currentPlan = customer?.subscription?.currentPlan || undefined
  const nextBillingCycleDate = customer?.subscription?.renewalDate || undefined
  const pendingScaToken = customer?.subscription?.pendingScaToken as
    | string
    | undefined

  const projectedCost = customer?.billingSummary?.nextBillingCycle?.totalAmount
  const credits = customer?.credits
  const paymentDetails = customer?.paymentDetails

  const workspacePartner =
    sketchPartnerData?.workspace.memberships.list?.entries?.[0]

  return (
    <WorkspaceSettingsLayout
      ref={elementToScrollTop}
      workspace={workspace}
      canUseSso={hasSSOFeature}
      hideNavbar={hideNavigation}
      canRender={canRender}
    >
      {status && (
        <BillingStatusBanners
          workspaceId={workspaceId}
          billingStatus={status}
          cancelPeriodEnd={subscriptionEnd}
          isUserAdmin={isAdmin}
          isEducationWorkspace={isEducationWorkspace}
        />
      )}
      <PanelWrapper>
        <Switch>
          <Route
            path={routes.WORKSPACE_SETTINGS_PEOPLE.template()}
            render={() => {
              if (isPersonalWorkspace) {
                /* There's already a FF inside there soo no need to touch it */
                return <PersonalWorkspaceSettingsPeople name={name} />
              }

              return (
                <WorkspaceSettingsPeopleContextProvider>
                  <WorkspaceSettingsPeople
                    workspaceId={identifier}
                    name={name}
                    avatar={avatarSrc}
                    role={workspace.userRole}
                    billingStatus={status!}
                    subscriptionEnd={subscriptionEnd}
                    seats={seats!}
                    /* Exclusive Administrative Props */
                    customerId={customerId!}
                    // These are optional because this page is common for both
                    // members and admins. This information is only available
                    // in PaymentQL which is queryable by admins only
                    currentPlan={currentPlan}
                    nextBillingCycleDate={nextBillingCycleDate}
                    paymentDetails={paymentDetails}
                    isSsoEnabled={ssoEnabled!}
                    isEducationWorkspace={isEducationWorkspace}
                    isIOS={isIOS}
                    showUpsell={showUpsell}
                    userDirectoryEnabled={
                      workspaceSettings.features.userDirectoryEnabled
                    }
                  />
                </WorkspaceSettingsPeopleContextProvider>
              )
            }}
            exact
          />
          {showWorkspaceSettingsGeneral && (
            <Route
              path={routes.WORKSPACE_SETTINGS_SETTINGS.template()}
              render={() => (
                <WorkspaceSettingsGeneral
                  workspaceId={identifier}
                  name={name}
                  avatar={avatarSrc}
                  areUploadsRestricted={
                    workspaceSettings.uploadsRestrictedFor !== 'NONE'
                  }
                  arePublicLinksRestricted={
                    workspaceSettings.features.restrictedPublicLinks
                  }
                  canUseRestrictedUploads={
                    workspaceSettings.canUseRestrictedUploads
                  }
                />
              )}
              exact
            />
          )}
          {showWorkspaceSettingsBilling && (
            <Route
              path={routes.WORKSPACE_SETTINGS_BILLING.template()}
              render={() => (
                <WorkspaceSettingsBilling
                  onWorkspaceSettingRefresh={refetch}
                  workspaceId={identifier}
                  workspaceName={name}
                  subscriptionStatus={status!}
                  subscriptionEnd={subscriptionEnd}
                  seats={seats!}
                  isFinance={isFinance}
                  isEducationWorkspace={isEducationWorkspace}
                  /* Exclusive Administrative Props */
                  credits={credits!}
                  customerId={customerId!}
                  currentPlan={currentPlan}
                  pendingSCAToken={pendingScaToken}
                  nextBillingCycleDate={nextBillingCycleDate}
                  billingDetails={customer!.billingDetails!}
                  paymentDetails={paymentDetails}
                  projectedCost={projectedCost}
                  trialEnd={trialEnd!}
                  isPartner={isPartner}
                  showUpsell={showUpsell}
                  partner={workspacePartner ? workspacePartner : null}
                />
              )}
              exact
            />
          )}
          {showWorkspaceSettingsSSO && (
            <Route
              path={routes.WORKSPACE_SETTINGS_SSO.template()}
              render={() => (
                <WorkspaceSettingsSso
                  customerId={customerId!}
                  workspaceId={identifier}
                  ssoShortName={ssoShortName || undefined}
                  isSsoEnabled={ssoEnabled!}
                  isScimEnabled={workspaceSettings.scimEnabled}
                  config={sso?.ssoConfig || undefined}
                  showUpsell={showUpsell}
                />
              )}
              exact
            />
          )}
          {isProfileCreated && isAdmin && (
            <Route
              path={routes.WORKSPACE_SETTINGS_PROFILE.template()}
              render={() => (
                <WorkspaceSettingsProfile workspaceIdentifier={workspaceId} />
              )}
              exact
            />
          )}
          <Redirect
            from={routes.WORKSPACE_SETTINGS.template()}
            to={routes.WORKSPACE_SETTINGS_PEOPLE.create({
              workspaceId,
            })}
          />
        </Switch>
      </PanelWrapper>
    </WorkspaceSettingsLayout>
  )
}

export default WorkspaceSettingsView
